import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { useBetaStatus } from "@/hooks/useBetaStatus";
import { BarChart3, BellRing, CheckCircle2, ExternalLink, Info, MessageSquareText, Vote } from "lucide-react";

/**
 * Reusable Community Poll widget: answer -> optional "why" -> results.
 * Privacy: email is optional (one answer per person), never shown anywhere.
 */
export const PollWidget = ({ poll, municipalityId, compact = false }) => {
  const beta = useBetaStatus();
  const [selected, setSelected] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [answered, setAnswered] = useState(null); // respond payload
  const [comment, setComment] = useState("");
  const [commentSent, setCommentSent] = useState(false);
  const [results, setResults] = useState(null);

  if (!poll) return null;
  const multi = poll.max_choices > 1;

  const toggle = (id) => {
    if (multi) {
      setSelected((s) => s.includes(id) ? s.filter((x) => x !== id)
        : s.length < poll.max_choices ? [...s, id] : s);
    } else {
      setSelected([id]);
    }
  };

  const loadResults = async () => {
    try {
      const params = {};
      if (poll.scope === "ontario" && municipalityId) params.municipality_id = municipalityId;
      const r = await api.get(`/public/polls/${poll.id}/results`, { params });
      setResults(r.data);
    } catch {
      toast.error("Could not load results");
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/public/polls/${poll.id}/respond`, {
        choices: selected,
        email: email.trim() || null,
        municipality_id: municipalityId || null,
        website: "",
      });
      setAnswered(r.data);
      await loadResults();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record your answer.");
      if (e.response?.status === 409) {
        await loadResults();
        setAnswered({ ok: false, duplicate: true });
      }
    } finally {
      setBusy(false);
    }
  };

  const sendComment = async () => {
    try {
      await api.post(`/public/polls/${poll.id}/comment`, {
        response_id: answered.response_id, comment: comment.trim(),
      });
      setCommentSent(true);
      toast.success("Thanks for explaining your choice");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your comment");
    }
  };

  return (
    <Card data-testid={`poll-widget-${poll.id}`}>
      <CardContent className={compact ? "p-5" : "p-6 sm:p-8"}>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 text-[10px]">
            {poll.category_label}
          </Badge>
          {poll.ward && <Badge variant="outline" className="text-[10px]">{poll.ward}</Badge>}
        </div>
        <h3 className={`font-heading font-bold text-foreground ${compact ? "text-base" : "text-lg sm:text-xl"}`} data-testid="poll-question">
          {poll.question}
        </h3>
        {poll.description && <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{poll.description}</p>}
        {poll.source?.name && (
          <details className="mt-2 group" data-testid="poll-why-asking">
            <summary className="text-[11px] text-primary cursor-pointer hover:underline list-none inline-flex items-center gap-1">
              <Info className="h-3 w-3" /> Why are we asking this?
            </summary>
            <div className="mt-1.5 rounded-md border border-slate-200 bg-slate-50 p-3 text-[11px] text-muted-foreground leading-relaxed">
              <p>This question relates to a documented local issue or decision.</p>
              <p className="mt-1"><span className="font-medium text-foreground">Source:</span> {poll.source.name}{poll.source.date ? ` (${poll.source.date})` : ""}</p>
              {poll.source.url && (
                <a href={poll.source.url} target="_blank" rel="noopener noreferrer"
                   className="text-primary hover:underline inline-flex items-center gap-1 mt-1" data-testid="poll-source-link">
                  View source <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </details>
        )}
        {multi && <p className="text-[11px] text-muted-foreground mt-1">Choose up to {poll.max_choices}.</p>}

        {!answered && !results && (
          <div className="mt-4">
            {multi ? (
              <div className="space-y-2" data-testid="poll-options">
                {poll.options.map((o) => (
                  <label key={o.id} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer text-sm transition-colors ${selected.includes(o.id) ? "border-accent bg-emerald-50/50" : "border-slate-200 hover:border-slate-300"}`}>
                    <Checkbox checked={selected.includes(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`poll-option-${o.id}`} />
                    {o.label}
                  </label>
                ))}
              </div>
            ) : (
              <RadioGroup value={selected[0] || ""} onValueChange={(v) => setSelected([v])} className="space-y-2" data-testid="poll-options">
                {poll.options.map((o) => (
                  <label key={o.id} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer text-sm transition-colors ${selected[0] === o.id ? "border-accent bg-emerald-50/50" : "border-slate-200 hover:border-slate-300"}`}>
                    <RadioGroupItem value={o.id} data-testid={`poll-option-${o.id}`} />
                    {o.label}
                  </label>
                ))}
              </RadioGroup>
            )}
            <div className="mt-3">
              <Label htmlFor={`poll-email-${poll.id}`} className="text-xs text-muted-foreground">Your email (optional — keeps it to one answer per person; never shown publicly)</Label>
              <Input id={`poll-email-${poll.id}`} type="email" className="mt-1" placeholder="you@example.com"
                     value={email} onChange={(e) => setEmail(e.target.value)} data-testid="poll-email-input" />
            </div>
            <Button className="mt-3 w-full sm:w-auto bg-accent hover:bg-accent/90" disabled={busy || selected.length === 0}
                    onClick={submit} data-testid="poll-submit-button">
              <Vote className="h-4 w-4 mr-2" /> {busy ? "Recording…" : "Submit My Answer"}
            </Button>
          </div>
        )}

        {answered?.ok && answered.follow_up && !commentSent && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/50 p-4" data-testid="poll-why-box">
            <p className="text-sm font-medium text-foreground flex items-center gap-2">
              <MessageSquareText className="h-4 w-4 text-primary" /> Why did you choose that? <span className="text-xs text-muted-foreground font-normal">(optional)</span>
            </p>
            <Textarea className="mt-2 bg-white" maxLength={300} rows={2} value={comment}
                      onChange={(e) => setComment(e.target.value)} placeholder="Up to 300 characters — anonymous"
                      data-testid="poll-comment-input" />
            <div className="flex items-center justify-between mt-2">
              <p className="text-[10px] text-muted-foreground">{comment.length}/300 · never linked to your name or declaration</p>
              <Button size="sm" variant="outline" disabled={comment.trim().length < 2} onClick={sendComment} data-testid="poll-comment-submit">Send</Button>
            </div>
          </div>
        )}

        {answered?.pita && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="poll-pita-cta">
            <p className="font-heading font-semibold text-sm text-amber-900">{answered.pita.heading}</p>
            <p className="text-xs text-amber-900/90 mt-1">{answered.pita.text}</p>
            <Button asChild size="sm" className="mt-2.5 bg-amber-600 hover:bg-amber-700 text-white" data-testid="poll-pita-button">
              <a href={answered.pita.url} target="_blank" rel="noopener noreferrer">
                {answered.pita.button} <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
              </a>
            </Button>
            <p className="text-[10px] text-amber-800/80 mt-2">{answered.pita.external_note}</p>
          </div>
        )}

        {/* PHASE 12 — Community Voice → launch-updates cross-promotion.
            Never auto-subscribes: participants must actively consent on the signup page. */}
        {answered?.ok && beta?.beta_mode && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/60 p-4" data-testid="poll-beta-crosspromo">
            <p className="font-heading font-semibold text-sm text-blue-950">WANT TO KNOW WHAT HAPPENS NEXT?</p>
            <p className="text-xs text-blue-950/80 mt-1">
              Join WitnessYourVote updates and we'll let you know when the full platform officially launches.
            </p>
            <Button asChild size="sm" className="mt-2.5 bg-primary hover:bg-primary/90 text-white" data-testid="poll-beta-crosspromo-cta">
              <Link to="/beta#signup"><BellRing className="h-3.5 w-3.5 mr-1.5" /> GET ON THE LIST</Link>
            </Button>
          </div>
        )}

        {results && (
          <div className="mt-4" data-testid="poll-results">
            {answered?.ok && (
              <p className="text-sm text-emerald-800 flex items-center gap-1.5 mb-3" data-testid="poll-answer-recorded">
                <CheckCircle2 className="h-4 w-4" /> Your voice has been counted.
                {results.available && results.response_count > 0 && (
                  <span className="text-muted-foreground font-normal">
                    {results.response_count} participating resident{results.response_count === 1 ? " has" : "s have"} answered.
                  </span>
                )}
              </p>
            )}
            {results.available ? (
              <>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <BarChart3 className="h-3.5 w-3.5" /> Responses: <span data-testid="poll-response-count">{results.response_count}</span>
                </p>
                <div className="space-y-2.5">
                  {results.options.map((o) => (
                    <div key={o.id}>
                      <div className="flex justify-between text-sm">
                        <span className="text-foreground">{o.label}</span>
                        <span className="tabular-nums text-muted-foreground">{o.percent}% ({o.count})</span>
                      </div>
                      <Progress value={o.percent} className="h-2 mt-1" />
                    </div>
                  ))}
                </div>
                {results.comment_themes?.length > 0 && (
                  <div className="mt-4" data-testid="poll-comment-themes">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Why people chose their answers</p>
                    <div className="flex flex-wrap gap-1.5">
                      {results.comment_themes.map((t) => (
                        <Badge key={t.theme} variant="outline" className="bg-secondary text-[11px]">
                          People mentioning {t.theme.toLowerCase()}: {t.count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="poll-results-threshold-note">{results.note}</p>
            )}
            <p className="text-[11px] text-muted-foreground leading-relaxed mt-4 border-t border-slate-100 pt-3" data-testid="poll-disclaimer">
              {results.disclaimer || poll.disclaimer}
            </p>
          </div>
        )}

        {!results && !answered && (
          <button className="text-xs text-primary hover:underline mt-3" onClick={loadResults} data-testid="poll-see-results-link">
            See results without answering
          </button>
        )}
      </CardContent>
    </Card>
  );
};
